import type { ClientMessage, CreateRoomRequest, ServerMessage } from '../shared/protocol.js';
import type { RaceFrame } from '../shared/snapshot-codec.js';
import type { MapDefinition, PlayerIdentity, PublicRoom } from '../shared/types.js';
import type { PersistedRoomState } from '../shared/race-session-state.js';
import { parsePersistedRoomState } from '../shared/persisted-room-state.js';
import { isClientMessage } from '../shared/client-message-schema.js';
import { isServerMessage } from '../shared/server-message-schema.js';
import { validateMapSet } from '../shared/map-cache.js';
import { raceEventMode } from '../shared/race-events.js';
import { raceWagerAvailable } from '../shared/gameplay-fun.js';
import { dominantNpcDifficulty } from '../shared/lobby.js';

export const LOCAL_PHYSICS_STEP = 1 / 60;
export const LOCAL_SNAPSHOT_INTERVAL_MS = 50;
export const LOCAL_CHECKPOINT_INTERVAL_MS = 2_000;
export const LOCAL_RACE_TTL_MS = 24 * 60 * 60 * 1_000;

export interface LocalRaceSetup {
  roomId: string;
  /** Monotonic IndexedDB ownership generation; assigned by the first durable
   * checkpoint so wall-clock changes cannot revive an older race. */
  offlineGeneration?: number;
  inviteToken: string;
  player: PlayerIdentity;
  payload: CreateRoomRequest;
  map: MapDefinition;
  snapshotIntervalMs?: number;
}

export type LocalWelcomeMessage = Extract<ServerMessage, { type: 'welcome' }>;

export type LocalRaceWorkerInbound =
  | { type: 'init'; setup: LocalRaceSetup; checkpoint?: PersistedRoomState }
  | { type: 'client-message'; message: ClientMessage }
  | { type: 'visibility'; hidden: boolean }
  | { type: 'pause'; paused: boolean }
  | { type: 'snapshot-interval'; intervalMs: number }
  | { type: 'close'; requestId: number };

export type LocalRaceWorkerOutbound =
  | { type: 'ready'; welcome: LocalWelcomeMessage }
  | { type: 'frame'; frame: RaceFrame }
  | { type: 'checkpoint'; state: PersistedRoomState; force?: boolean }
  | { type: 'closed'; requestId: number; state?: PersistedRoomState }
  | { type: 'server-message'; message: ServerMessage }
  | { type: 'fatal'; message: string };

export function isLocalRaceWorkerInbound(value: unknown): value is LocalRaceWorkerInbound {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  switch (value.type) {
    case 'init':
      return isLocalRaceSetup(value.setup)
        && (
          value.checkpoint === undefined
          || Boolean(parsePersistedRoomState(value.checkpoint, value.setup.roomId))
        );
    case 'client-message':
      return isClientMessage(value.message);
    case 'visibility':
      return typeof value.hidden === 'boolean';
    case 'pause':
      return typeof value.paused === 'boolean';
    case 'snapshot-interval':
      return finitePositive(value.intervalMs);
    case 'close':
      return Number.isInteger(value.requestId) && Number(value.requestId) > 0;
    default:
      return false;
  }
}

export function isLocalRaceWorkerOutbound(
  value: unknown,
  expectedRoomId: string,
): value is LocalRaceWorkerOutbound {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  switch (value.type) {
    case 'ready':
      return isServerMessage(value.welcome) && value.welcome.type === 'welcome';
    case 'frame':
      return isServerMessage(value.frame) && value.frame.type === 'frame';
    case 'checkpoint':
      return Boolean(parsePersistedRoomState(value.state, expectedRoomId))
        && (value.force === undefined || typeof value.force === 'boolean');
    case 'closed':
      return Number.isInteger(value.requestId)
        && Number(value.requestId) > 0
        && (
          value.state === undefined
          || Boolean(parsePersistedRoomState(value.state, expectedRoomId))
        );
    case 'server-message':
      return isServerMessage(value.message);
    case 'fatal':
      return typeof value.message === 'string';
    default:
      return false;
  }
}

export function initialLocalRoom(setup: LocalRaceSetup, createdAt = Date.now()): PublicRoom {
  return {
    id: setup.roomId,
    hostPlayerId: setup.player.id,
    settings: localRaceSettings(setup.payload, setup.map),
    phase: 'lobby',
    raceAttempt: 1,
    playerCount: 1,
    createdAt,
    expiresAt: createdAt + LOCAL_RACE_TTL_MS,
  };
}

export function localRaceSettings(payload: CreateRoomRequest, map: MapDefinition): PublicRoom['settings'] {
  const mode = raceEventMode(payload);
  const roster = mode === 'time-trial'
    ? []
    : payload.npcRoster?.slice(0, 11) ?? Array.from({ length: payload.npcCount }, () => payload.npcDifficulty);
  return {
    mapId: map.id,
    npcCount: roster.length,
    npcDifficulty: payload.npcRoster ? dominantNpcDifficulty(roster, payload.npcDifficulty) : payload.npcDifficulty,
    npcRoster: roster,
    ...(payload.laps !== undefined ? { laps: payload.laps } : {}),
    maxPlayers: 1,
    antiCheat: payload.antiCheat,
    collisions: mode === 'road-rage' ? true : payload.collisions,
    traffic: payload.traffic ?? false,
    timeOfDay: payload.timeOfDay ?? map.timeOfDay,
    weather: payload.weather ?? map.weather,
    timeTrial: mode === 'time-trial',
    eventMode: mode,
    arcadePreset: payload.arcadePreset ?? 'standard',
    tourId: payload.tourId,
    tourStage: payload.tourStage,
    featuredRival: payload.featuredRival,
    wager: raceWagerAvailable(
      payload.wager,
      map,
      mode,
      1 + roster.length,
    ) ? payload.wager : undefined,
    dailyChallengeId: payload.dailyChallengeId,
    // Rewind is a playful offline race tool, not a way to edit a competitive
    // ghost or daily time. Time trials therefore keep strict lap integrity.
    rewindLimit: mode === 'time-trial' ? 0 : 2,
  };
}

function isLocalRaceSetup(value: unknown): value is LocalRaceSetup {
  if (!isRecord(value) || !isRecord(value.player) || !isRecord(value.payload)) return false;
  return identifier(value.roomId)
    && token(value.inviteToken)
    && identifier(value.player.id)
    && token(value.player.token)
    && typeof value.player.name === 'string'
    && identifier(value.player.carId)
    && isRecord(value.player.tuning)
    && typeof value.payload.mapId === 'string'
    && validateMapSet([value.map]) !== null
    && value.payload.mapId === (value.map as MapDefinition).id
    && (value.snapshotIntervalMs === undefined || finitePositive(value.snapshotIntervalMs));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function identifier(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function token(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,200}$/.test(value);
}

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
